import './checkin-summary.scss';

import {
  AutoCheckedOut,
  CheckInSummary,
  CheckInSummaryTableColumn,
  CheckOut,
  CheckOutusingNFC,
  DISTACE_TO_TRIGGER_CHECKIN_SCREEN,
  DISTANCE_TO_TRIGGER_VENDOR_PURPOSE_API,
  LocationSrvcsHeading,
  TotalCheckIns,
  TotalCheckouts,
  TotalDuration
} from '../../../shared/constants/constants';
import { CheckInSummaryState, fetchCheckinsSummaryDataAsync } from '../../../redux/reducers/checkInSummarySlice';
import {
  GreenText,
  NavbarContainer,
  NavbarSpacer,
  NavbarTitle,
  NotificationBadge,
  NotificationButton,
  Table,
  TableContainer,
  Td,
  Th
} from './checkin-summary.styled';
import { NotificationState, fetchMessagesAsync } from '../../../redux/reducers/notificationSlice';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { StoreInfoState, fetchStoreInfoDataAsync } from '../../../redux/reducers/storeInfoSlice';
import { convertSecondsToHours, formatDate, formatTime } from '../../../shared/format-date/format-date';
import { getDeviceToken, getStoreId, getXSwyTimeZone, saveStoreId } from '../../../services/token.service';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import BannerBottom from '../banner-bottom/banner-bottom';
import { BellIcon } from '@heroicons/react/24/outline';
import { RootState } from '../../../config/redux/store';
import SuccessButton from '../../../shared/styled-components/styled-buttons';
import { checkLastCheckIn } from '../../../shared/validations/validations';
import { openAlertAsync } from '../../../redux/reducers/alertSlice';

const CheckInSummaryPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const locationLib = useLocation();

  const [vendorName, setVendorName] = useState<string | null>(null);
  const [location, setLocation] = useState<{ lat: number | null; lng: number | null }>({ lat: null, lng: null });
  const [distanceWithinHundredMts, setDistanceWithinHundredMts] = useState<boolean>(false);
  const [triggerGeolocation, setTriggerGeolocation] = useState<boolean>(false);
  const [distanceWithinThreshold, setDistanceWithinThreshold] = useState<boolean>(false);

  const { storeInfo } = useSelector<RootState, StoreInfoState>((state) => state.globalVendorCheckIn.storeInfo);

  const { checkInSummary } = useSelector<RootState, CheckInSummaryState>(
    (state) => state.globalVendorCheckIn.checkInSummary
  );

  // Get notification state for unread count
  const { messages } = useSelector<RootState, NotificationState>((state) => state.globalVendorCheckIn.notifications);

  const { checkIns, totalCheckins, totalCheckouts, totalDuration, isLoading } = checkInSummary;

  const storeIdFromParams = searchParams.get('storeId') as string;

  const handleEnableClick = () => {
    setTriggerGeolocation(!triggerGeolocation);
  };

  const handleCheckout = () => {
    navigate('/checkout');
  };

  // Calculate unread message count with memoization for performance
  const unreadCount = useMemo(() => {
    return messages.filter((message) => !message.isRead).length;
  }, [messages]);

  // Handle notification button click - only allow if user is checked in
  const handleNotificationClick = () => {
    navigate('/message-center');
  };

  useEffect(() => {
    getDeviceToken().then((token) => {
      if (!token) {
        navigate('/');
      } else {
        dispatch(fetchCheckinsSummaryDataAsync({ deviceToken: token }));
      }
    });
  }, [dispatch, navigate]);

  useEffect(() => {
    if (storeInfo.stores?.length > 0 && storeInfo.stores[0].storeId) {
      saveStoreId(storeInfo.stores[0].storeId);
    } else if (storeIdFromParams) {
      // Only save if storeIdFromParams has a valid value
      saveStoreId(storeIdFromParams);
    }
    // If neither condition is met, preserve the existing storeId in localStorage
  }, [storeInfo.stores, storeIdFromParams]);

  // Fetch messages when component mounts (using vendorUserId from checkin data)
  useEffect(() => {
    // Get vendorUserId from the most recent check-in data
    const mostRecentCheckin = checkIns && checkIns.length > 0 ? checkIns[0] : null;
    const vendorUserId = mostRecentCheckin?.vendorUser?.vendorUserId;

    if (vendorUserId) {
      getDeviceToken().then((token) => {
        if (token) {
          dispatch(
            fetchMessagesAsync({
              vendorUserId: vendorUserId,
              deviceToken: token,
              headers: {
                'x-swy-timezone': getXSwyTimeZone()
              }
            })
          );
        }
      });
    }
  }, [dispatch, checkIns]);

  const prevLocation = useRef<{ lat: number | null; lng: number | null }>({ lat: null, lng: null });

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371e3; // metres
    const φ1 = (lat1 * Math.PI) / 180;
    const φ2 = (lat2 * Math.PI) / 180;
    const Δφ = ((lat2 - lat1) * Math.PI) / 180;
    const Δλ = ((lon2 - lon1) * Math.PI) / 180;

    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    const distance = R * c; // in metres
    return distance;
  };

  useEffect(() => {
    if (navigator.geolocation) {
      const watcher = navigator.geolocation.watchPosition(
        (position) => {
          const newLocation = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
            // lat: 37.696502273709186,
            // lng: -121.9385954680138
          };
          if (prevLocation.current.lat === null && prevLocation.current.lng === null) {
            // First fetched coordinates on page load
            dispatch(fetchStoreInfoDataAsync(newLocation));
            // for local testing
            // dispatch(fetchStoreInfoDataAsync({ lat: 43.64735256, lng: -116.3520867 }));
          } else {
            const distance = calculateDistance(
              prevLocation.current.lat,
              prevLocation.current.lng,
              newLocation.lat,
              newLocation.lng
            );

            if (distance > DISTANCE_TO_TRIGGER_VENDOR_PURPOSE_API) {
              dispatch(fetchStoreInfoDataAsync(newLocation));
              //for local testing
              // dispatch(fetchStoreInfoDataAsync({ lat: 37.696502273709186, lng: -121.9385954680138 }));
            }
          }
          prevLocation.current = newLocation;
          setLocation(newLocation);
        },
        async (error) => {
          console.error('Error watching position:', error);
          getStoreId().then((storeId) => {
            if (storeId) {
              dispatch(fetchStoreInfoDataAsync({ storeId }));
            } else {
              dispatch(openAlertAsync('Please enable location services or provide a store ID to check in.'));
            }
          });
        },
        {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 0
        }
      );

      // Cleanup watcher on component unmount
      return () => {
        navigator.geolocation.clearWatch(watcher);
      };
    } else {
      console.error('Geolocation is not supported by this browser.');
      dispatch(openAlertAsync('Geolocation is not supported by this browser.'));
    }
  }, [dispatch, triggerGeolocation]);

  useEffect(() => {
    if (checkIns && checkIns?.length > 0) {
      const vendorName = checkIns[0]?.vendorUser?.vendor?.company;
      setVendorName(vendorName);
    }
  }, [checkIns]);

  useEffect(() => {
    const checkDistanceAndNavigate = () => {
      if (storeInfo.stores?.length > 0) {
        const distance = calculateDistance(
          location.lat,
          location.lng,
          storeInfo.stores[0].latitude,
          storeInfo.stores[0].longitude
        );
        //for testing updated the distance to 500
        if (distance <= DISTACE_TO_TRIGGER_CHECKIN_SCREEN) {
          setDistanceWithinHundredMts(true);
          setDistanceWithinThreshold(true);
        }
      }
    };

    checkDistanceAndNavigate();
  }, [location.lat, location.lng, navigate, storeInfo.stores, distanceWithinHundredMts, checkIns]);

  useEffect(() => {
    if (checkIns) {
      if (!isLoading && (distanceWithinThreshold || storeIdFromParams) && !checkLastCheckIn(checkIns)) {
        navigate(`/checkin-detail${locationLib.search}`);
      }
    }
  }, [checkIns, distanceWithinThreshold, navigate, isLoading, storeIdFromParams, locationLib.search]);

  const renderCheckoutStatus = (
    checkoutTs: string | null,
    autoCheckedOut: boolean,
    distanceWithinHundredMts: boolean
  ) => {
    if (checkoutTs) {
      return formatTime(checkoutTs);
    } else if (autoCheckedOut) {
      return <strong>{AutoCheckedOut}</strong>;
    } else {
      return distanceWithinHundredMts ? <SuccessButton onClick={handleCheckout}>{CheckOut}</SuccessButton> : 'N/A';
    }
  };

  return (
    <div className='checkin-summary-page'>
      <NavbarContainer>
        <NavbarSpacer />
        <NavbarTitle>{CheckInSummary}</NavbarTitle>
        <NotificationButton
          onClick={handleNotificationClick}
          aria-label={unreadCount > 0 ? `Notifications (${unreadCount} unread)` : 'Notifications'}
          title='View notifications'
        >
          <BellIcon />
          {unreadCount > 0 && <NotificationBadge count={unreadCount} />}
        </NotificationButton>
      </NavbarContainer>
      <div className='checkin-summary-content'>
        <h1>{vendorName}</h1>
        <div className='total-box'>
          <div>
            <span className='heading'>{TotalDuration}</span>
            <span className='timebox'>{convertSecondsToHours(totalDuration)}</span>
          </div>
          <div>
            <span className='heading'>{TotalCheckIns}</span>
            <span className='timebox'>{totalCheckins}</span>
          </div>
          <div>
            <span className='heading'>{TotalCheckouts}</span>
            <span className='timebox'>{totalCheckouts}</span>
          </div>
        </div>

        {checkIns && checkIns.length > 0 && (
          <TableContainer className='table-responsive'>
            <Table>
              <thead>
                <tr>
                  {CheckInSummaryTableColumn.map((column) => {
                    return (
                      <Th key={column.id}>
                        <span className='flex items-center'>
                          {column.icon} {column.name}
                        </span>
                      </Th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {checkIns.map((checkIn) => {
                  const { checkinId, storeId, checkinTs, checkoutTs, autoCheckedOut } = checkIn;
                  return (
                    <tr key={checkinId}>
                      <Td>{storeId}</Td>
                      <Td>{formatDate(checkinTs)}</Td>
                      <Td>
                        <GreenText>{checkinTs ? formatTime(checkinTs) : 'N/A'}</GreenText>
                      </Td>
                      <Td>
                        <GreenText>
                          {' '}
                          {renderCheckoutStatus(checkoutTs as string, autoCheckedOut, distanceWithinHundredMts)}
                        </GreenText>
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </TableContainer>
        )}
      </div>
      {(!location?.lat || !location?.lng) && (
        <BannerBottom heading={LocationSrvcsHeading} subtext={CheckOutusingNFC} onEnableClick={handleEnableClick} />
      )}
    </div>
  );
};

export default CheckInSummaryPage;
